/* eslint-disable no-script-url */

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Page } from '@accedo/vdkweb-tv-ui';
import { focusManager } from '@accedo/vdkweb-navigation';
import cx from 'classnames';
import { Image, Shimmer } from 'react-shimmer';
import { actions as modalActions } from '#/redux/modules/modal/actions';
import {
  ASYNC_SWITCH_PROFILE,
  logicalSwitchToChildProfile,
  switchToParentProfile,
  checkParentalTimeLimit,
} from '#/redux/modules/identity/actions';
import {
  getSavedProfiles,
  getCurrentProfile,
  getProfileSkip,
} from '#/redux/modules/onboarding/selectors';
import {
  actions as onboardingActions,
  newDraftProfile,
} from '#/redux/modules/onboarding/actions';
import { sendScreenView } from '#/redux/modules/firestore/actions';
import {
  getSensicalTvConfig,
  getWhiteLogoUri,
  getAuthenticateUserProfileContent,
  getAgeGroupConfig,
} from '#/redux/modules/accedoOne/selectors';
import { isVIZIO, isTizen } from '#/utils/componentStyleConfig';
import firestoreApi from '#/services/firestore/api';
import { FocusDiv } from '#/widgets/ui';
import {
  authScreenNames,
  customDataFieldTypes,
  guestTileTitle,
} from '#/config/constants';
import { PROFILESELECTION, getHorizontalNav } from '#/utils/navigationHelper';
import GreenBackground from '#/static/images/background-images/Green.svg';
import GreenBackgroundTizen from '#/static/images/background-images/BgProfileSelection.png';
import Logo from '#/static/images/icons/logo-login.svg';
import ParentZone from '#/static/images/icons/menu-parent-zone-white.svg';
import routes, { mainRoutes } from '#/utils/routes';
import { ROLES, setSpeech } from '#/utils/accessibility';
import { getGuestLocalStorage } from '#/redux/modules/guest/selectors';
import { getAgeRange } from '#/utils/getAgeRange';
import { isOpen } from '#/redux/modules/modal/selectors';
import styles from './TVProfileSelection.scss';
import ProfileCard from '../../ProfileCard/ProfileCard';
import ErrorDispatcher from '../../ErrorDispatcher/ErrorDispatcher';

const { LIVETV } = mainRoutes;

const PROFILE_CARD = 'PROFILE_CARD';
const PROFILE_CARDS_CONTAINER = 'CARDS_CONTAINER';
const { BOOKMARKS_V2, RECENTLY_WATCHED_V2 } = customDataFieldTypes;

const scrollIndexRight = 2;
const scrollIndexLeft = 3;

// Some Tizen TVs have trouble rendering huge embedded svg as background images on divs so we replaced them with png
const tizenBgStyle = {
  backgroundPosition: 'center center',
  backgroundImage: `url(${GreenBackgroundTizen})`,
};

const regularBgStyle = {
  backgroundImage: `url(${GreenBackground})`,
};

const hasCustomData = obj => {
  return !!obj?.[BOOKMARKS_V2]?.length || !!obj?.[RECENTLY_WATCHED_V2]?.length;
};

const ageGroupObj = {
  2: {
    max: 4,
    min: 2,
  },
  5: {
    max: 7,
    min: 5,
  },
  8: {
    max: 10,
    min: 8,
  },
};

const GuestCard = ({
  ageGroupValue = 0,
  contentImageUrl,
  tileDescription,
  typeOfContent,
  tagBackgroundColor,
  labelText,
  pillPosition,
  nav,
  selectProfile,
}) => {
  const renderImage = (imageUrl, label, zoneStyle) => {
    return (
      <div className={`${styles.imageZone} ${imageUrl ? zoneStyle : ''}`}>
        <div
          className={cx(styles.imageZoneCont, {
            [styles.tvImage]: isTizen() || isVIZIO(),
          })}
        >
          <Image src={imageUrl} fallback={<Shimmer />} />
        </div>
        {label && pillPosition && tagBackgroundColor && (
          <h6
            className={`${styles.tagCard} ${
              pillPosition === 'top-left' ? styles.topLeft : ''
            } ${pillPosition === 'bottom-left' ? styles.bottomLeft : ''} ${
              pillPosition === 'top-right' ? styles.topRight : ''
            } ${pillPosition === 'bottom-right' ? styles.bottomRight : ''}`}
            style={{ backgroundColor: tagBackgroundColor }}
          >
            {label}
          </h6>
        )}
      </div>
    );
  };
  return (
    <div aria-label={`${tileDescription}.`} className={styles.card}>
      <FocusDiv
        nav={nav}
        className={`${styles.cardTitle}`}
        classNameFocused={styles.tvFocused}
        onClick={() => selectProfile(typeOfContent, ageGroupValue)}
      >
        {renderImage(contentImageUrl, labelText, styles.desktopImageZone)}
        {tileDescription && (
          <h2 className={styles.desktopLabel}>{tileDescription}</h2>
        )}
      </FocusDiv>
    </div>
  );
};

GuestCard.propTypes = {
  ageGroupValue: PropTypes.number,
  contentImageUrl: PropTypes.string,
  tileDescription: PropTypes.string,
  typeOfContent: PropTypes.string,
  tagBackgroundColor: PropTypes.string,
  labelText: PropTypes.string,
  pillPosition: PropTypes.string,
  nav: PropTypes.object,
  selectProfile: PropTypes.func,
};
const Cards = ({
  history,
  authenticateUserProfileContent,
  profiles,
  addProfile,
}) => {
  const dispatch = useDispatch();
  const ageGroupConfig = useSelector(getAgeGroupConfig);
  const onSwitch = useCallback(async profileId => {
    const timeLimitReached = await dispatch(
      checkParentalTimeLimit({ profileId }),
    );
    if (!timeLimitReached) {
      firestoreApi.setUserId(profileId);
      await dispatch(logicalSwitchToChildProfile(profileId));
      await dispatch(sendScreenView(authScreenNames.PROFILE_SELECTION));
      history.push(routes.HOME);
    }
  }, []);
  const selectProfile = useCallback(
    async (type, age) => {
      if (type === 'live') {
        const pathname = `${LIVETV}/${age}`;
        const newEntry = {
          pathname,
          state: { fromProfileSelectionPage: routes.PROFILE_SELECTION },
        };
        history.replace('/');
        history.push(newEntry);
      } else {
        const reducedProfiles = profiles.filter(profile => {
          const {
            parentalTimeLimit = 0,
            timeWatched: { duration: durationWatched = 0 } = {},
          } = profile;

          const timeLimitReached =
            parentalTimeLimit !== 0 && durationWatched > parentalTimeLimit;

          return (
            profile.age >= ageGroupObj[age].min &&
            profile.age <= ageGroupObj[age].max &&
            !timeLimitReached
          );
        });
        if (reducedProfiles.length === 1) {
          const getFirstProfileId = reducedProfiles[0].id;
          onSwitch(getFirstProfileId);
        } else if (reducedProfiles.length > 0) {
          dispatch(
            modalActions.openModal({
              modalId: 'ProfileToWatchModal',
              modalData: {
                onSwitch,
                reducedProfiles,
              },
            }),
          );
        } else {
          await dispatch(newDraftProfile());
          dispatch(
            modalActions.openModal({
              modalId: 'ProfileToWatchNoneModal',
              modalData: {
                addProfile,
                age,
                name: `Guest ${age}up`,
                returnFocus: PROFILESELECTION.CARDS_CONTAINER,
              },
              finallyAction: () => {
                focusManager.changeFocus(PROFILESELECTION.CARDS_CONTAINER);
              },
            }),
          );
        }
      }
    },
    [profiles],
  );
  const guestCards = useMemo(() => {
    const getAllKeyEntriesByContent = Object.keys(
      authenticateUserProfileContent.setAllEntriesByContent,
    );
    const getAgeGroupConfigValues = Object.values(ageGroupConfig || []);

    return Object.values(
      authenticateUserProfileContent.setAllEntriesByContent ?? {},
    ).map((groupContent, index, array) => {
      const guestCardsCountCount = array.reduce((t, a) => t + a.length, 0);
      return (
        <div
          className={styles.guestTitleText}
          key={getAllKeyEntriesByContent[index]}
        >
          <h2 className={styles.guestHorizontalLine}>
            {guestTileTitle[getAllKeyEntriesByContent?.[index]]}
          </h2>
          <div>
            {groupContent.map((group, idx) => {
              const {
                ageGroupValue,
                contentImageUrl,
                contentImageLargeUrl,
                contentImageSmallUrl,
                tileDescription,
                tileDescriptionMobile,
                title,
                typeOfContent,
                tagBackgroundColor,
                labelText,
                labelTextTablet,
                labelTextMobile,
                pillPosition,
              } = group;
              const {
                minAge = '',
                maxAge = '',
                noRegistrationConfig: { info1, info2, info3 } = {},
              } = ageGroupValue
                ? getAgeGroupConfigValues.find(
                    c => c.ageGroupValue === ageGroupValue,
                  )
                : {};
              const ageRange = minAge && maxAge ? `${minAge}-${maxAge}` : '';
              const cardIndex = index * (array.length + 1) + idx;
              return (
                <GuestCard
                  history={history}
                  key={cardIndex}
                  nav={{
                    id: `${PROFILE_CARD}-${cardIndex}`,
                    nextleft:
                      cardIndex === 0
                        ? undefined
                        : `${PROFILE_CARD}-${cardIndex - 1}`,
                    nextright:
                      cardIndex + 1 === guestCardsCountCount
                        ? undefined
                        : `${PROFILE_CARD}-${cardIndex + 1}`,
                    nextup: PROFILESELECTION.CARDS_CONTAINER,
                  }}
                  {...{
                    ageGroupValue,
                    contentImageUrl,
                    contentImageLargeUrl,
                    contentImageSmallUrl,
                    tileDescription,
                    tileDescriptionMobile,
                    title,
                    typeOfContent,
                    cardIndex,
                    ageRange,
                    info1,
                    info2,
                    info3,
                    tagBackgroundColor,
                    labelText,
                    labelTextTablet,
                    labelTextMobile,
                    pillPosition,
                    selectProfile,
                  }}
                />
              );
            })}
          </div>
        </div>
      );
    });
  }, [authenticateUserProfileContent, ageGroupConfig]);

  return (
    <FocusDiv
      nav={{
        id: PROFILE_CARDS_CONTAINER,
        forwardFocus: `${PROFILE_CARD}-0`,
      }}
      className={styles.cardsArea}
    >
      {guestCards}
    </FocusDiv>
  );
};
Cards.propTypes = {
  history: PropTypes.object,
  authenticateUserProfileContent: PropTypes.object,
  profiles: PropTypes.object,
  addProfile: PropTypes.func,
};

const ProfileSelection = ({ history }) => {
  const dispatch = useDispatch();
  const hasPageBeenRendered = useRef(false);
  const { logoUri = Logo } = useSelector(getSensicalTvConfig) || {};
  const isModalOpen = useSelector(state => isOpen(state));
  const baseballCardsContainer = useRef(null);
  const [focusedProfile, setFocusedProfile] = useState(null);
  const currentProfile = useSelector(getCurrentProfile);
  const profiles = useSelector(getSavedProfiles);
  const [scrollLength, setScrollLength] = useState(160);
  const whiteLogoUri = useSelector(getWhiteLogoUri) || '';
  const authenticateUserProfileContent = useSelector(
    getAuthenticateUserProfileContent,
  );
  const profileSkip = useSelector(getProfileSkip);

  const [addProfileNav, setAddProfileNav] = useState({
    id: PROFILESELECTION.ADD_PROFILE_BTN,
    nextup: PROFILESELECTION.CARDS_CONTAINER,
    nextdown: PROFILESELECTION.PRIVACY_POLICY,
    parent: PROFILESELECTION.PAGE,
  });

  const guestCustomData = useSelector(getGuestLocalStorage);

  useEffect(() => {
    // Prevent focus loss when there are no profiles
    if (!profiles?.length) {
      setAddProfileNav(prev => ({ ...prev, nextup: null }));
      focusManager.changeFocus(PROFILESELECTION.ADD_PROFILE_BTN);
    } else if (addProfileNav.nextup !== PROFILESELECTION.CARDS_CONTAINER) {
      setAddProfileNav(prev => ({
        ...prev,
        nextup: PROFILESELECTION.CARDS_CONTAINER,
      }));
    }
  }, [profiles?.length]);

  useEffect(() => {
    setScrollLength(
      document.getElementById(PROFILESELECTION.CARDS_CONTAINER).scrollWidth /
        profiles.length,
    );
  }, [baseballCardsContainer.current]);

  const editAvatar = useCallback(id => {
    dispatch(onboardingActions.editProfile({ profileId: id }));
    dispatch(
      modalActions.openModal({
        modalId: 'AvatarModalTV',
        successAction: {
          type: String(onboardingActions.setProfileAvatar),
        },
        finallyAction: {
          type: String(onboardingActions.discardProfile),
        },
        modalData: {
          returnFocus: PROFILESELECTION.CARDS_CONTAINER,
        },
      }),
    );
  }, []);

  const addProfile = useCallback((name, avatar) => {
    dispatch(
      modalActions.openModal({
        modalId: 'PinModal',
        modalData: {
          isNewAccountMobile: false,
        },
        successAction: async pin => {
          const newEntry = {
            pathname: routes.PROFILE_ADDITION,
            state: {
              name,
              avatar,
            },
          };
          await dispatch(switchToParentProfile({ credential: pin }));
          await dispatch(newDraftProfile());
          history.push(newEntry);
        },
      }),
    );
  }, []);

  const onSwitch = useCallback(async profileId => {
    const timeLimitReached = await dispatch(
      checkParentalTimeLimit({ profileId }),
    );
    if (!timeLimitReached) {
      firestoreApi.setUserId(profileId);
      await dispatch(logicalSwitchToChildProfile(profileId));
      await dispatch(sendScreenView(authScreenNames.PROFILE_SELECTION));
      history.push(routes.HOME);
    }
  }, []);

  const onProfileSelection = useCallback(
    (profile, ageRange, requestSyncUp) => {
      if (requestSyncUp) {
        dispatch(
          modalActions.openModal({
            modalId: 'SyncCustomDataModal',
            modalData: {
              profileId: profile?.id,
              ageRange,
              saveProfile: () => onSwitch(profile?.id),
            },
          }),
        );
        return;
      }

      // Continue with regular flow
      onSwitch(profile?.id);
    },
    [onSwitch],
  );

  // todo: maybe refactor to swimlane/scroll component
  const handleScroll = (prev, next) => {
    if (prev < next && next > scrollIndexRight) {
      requestAnimationFrame(() => {
        document.getElementById(PROFILESELECTION.CARDS_CONTAINER).scrollLeft =
          scrollLength * next;
      });
    } else if (prev > next && next < profiles.length - scrollIndexLeft) {
      requestAnimationFrame(() => {
        document.getElementById(PROFILESELECTION.CARDS_CONTAINER).scrollLeft =
          scrollLength * next;
      });
    }
  };

  const onSwitchToParentZone = useCallback(() => {
    dispatch(
      modalActions.openModal({
        modalId: 'PinModal',
        modalData: {
          isNewAccountMobile: false,
        },
        successAction: async pin => {
          const rand_index = Math.floor(Math.random() * profiles.length);
          const rand_profile = profiles[rand_index];
          await dispatch(switchToParentProfile({ credential: pin }));
          dispatch(onboardingActions.setSelectedProfile(rand_profile.id));
          history.push(mainRoutes.PARENT_ZONE);
        },
      }),
    );
  }, []);

  const handleAddProfile = useCallback(() => {
    dispatch(
      modalActions.openModal({
        modalId: 'PinModal',
        modalData: {
          isNewAccountMobile: false,
        },
        successAction: async pin => {
          await dispatch(switchToParentProfile({ credential: pin }));
          await dispatch(newDraftProfile());
          history.push(routes.PROFILE_ADDITION);
        },
      }),
    );
  }, []);

  const baseballCards = useMemo(() => {
    const maxProfiles = profiles.length;
    const components = profiles
      ? profiles?.map((item, index) => `ITEM-${index}`)
      : [];
    components.push(`ITEM-${maxProfiles}`);
    const profilesNavMap = {
      ...getHorizontalNav(components, {}),
    };
    const reducedProfiles = profiles.map((profile, index) => {
      const {
        parentalTimeLimit = 0,
        timeWatched: { duration: durationWatched = 0 } = {},
      } = profile;

      const timeLimitReached =
        parentalTimeLimit !== 0 && durationWatched > parentalTimeLimit;

      const ageRange = getAgeRange(profile?.age);
      const localCustomData = guestCustomData[ageRange];
      const hasLocalCustomData = hasCustomData(localCustomData);
      const hasEmptyCustomData = !hasCustomData(profile);
      const requesSyncUp = hasEmptyCustomData && hasLocalCustomData;

      return (
        <div>
          <ProfileCard
            locked={timeLimitReached}
            key={profile.id}
            profile={profile}
            editMode={false} // bypass check so can edit without changing card style
            onSwitch={() => {
              onProfileSelection(profile, ageRange, requesSyncUp);
            }}
            onClick={async () => {
              onProfileSelection(profile, ageRange, requesSyncUp);
            }}
            onEdit={() => editAvatar(profile.id)}
            nav={{
              ...profilesNavMap[`ITEM-${index}`],
              parent: PROFILESELECTION.CARDS_CONTAINER,
            }}
            onFocus={() => {
              baseballCardsContainer.current?.classList?.add(styles.scrollX);
              const prev = focusedProfile;
              setFocusedProfile(index);
              handleScroll(prev, index);
              setSpeech(`${profile.name}, ${ROLES.BUTTON}`);
            }}
            onBlur={() => {
              baseballCardsContainer.current?.classList?.remove(styles.scrollX);
            }}
          />
        </div>
      );
    });
    reducedProfiles.push(
      <ProfileCard
        locked={false}
        key={maxProfiles}
        profile={{ id: maxProfiles, name: 'Add New', avatar: null }}
        isDummy
        parentClick={handleAddProfile}
        nav={{
          ...profilesNavMap[`ITEM-${maxProfiles}`],
          parent: PROFILESELECTION.CARDS_CONTAINER,
        }}
        onClick={async () => {
          handleAddProfile();
        }}
        onFocus={() => {
          baseballCardsContainer.current?.classList?.add(styles.scrollX);
          const prev = focusedProfile;
          setFocusedProfile(maxProfiles);
          handleScroll(prev, maxProfiles);
          setSpeech(`Add New, ${ROLES.BUTTON}`);
        }}
        onBlur={() => {
          baseballCardsContainer.current?.classList?.remove(styles.scrollX);
        }}
      />,
    );
    return reducedProfiles;
  }, [profiles, currentProfile, focusedProfile]);

  useEffect(() => {
    profiles.forEach(({ id: profileId }) => {
      dispatch(checkParentalTimeLimit({ profileId, showModal: false }));
    });
    firestoreApi.updateDefaultUserProperties({
      age_group: 'parent',
    });
  }, []);

  useEffect(() => {
    if (profiles.length > 0 && profileSkip) {
      const skipUser = profiles.find(p => p.name === profileSkip.payload);
      if (skipUser) {
        dispatch(onboardingActions.setProfileSkip(''));
        onSwitch(skipUser.id);
      }
    }
  }, [profiles, profileSkip]);

  useEffect(() => {
    if (hasPageBeenRendered.current) {
      if (
        !isModalOpen &&
        focusManager.getCurrentFocus() !== PROFILE_CARDS_CONTAINER
      ) {
        focusManager.changeFocus(PROFILE_CARDS_CONTAINER);
      }
      return;
    }
    hasPageBeenRendered.current = true;
  }, [isModalOpen]);

  return (
    <Page
      className={styles.profileSelection}
      style={isTizen() ? tizenBgStyle : regularBgStyle}
      nav={{
        id: PROFILESELECTION.PAGE,
        forwardFocus: PROFILESELECTION.CARDS_CONTAINER,
        useLastFocus: true,
      }}
    >
      <ErrorDispatcher errorId={ASYNC_SWITCH_PROFILE} />
      <div className={styles.heading}>
        <img
          className={styles.logoWebLayout}
          src={whiteLogoUri}
          style={{ display: logoUri || whiteLogoUri ? 'inline-block' : 'none' }}
          loading="eager"
          alt="Logo"
        />
        <FocusDiv
          className={styles.parentalZoneContLarge}
          classNameFocused={styles.tvFocused}
          onClick={onSwitchToParentZone}
          nav={{
            id: 'PARENTALZONE',
            nextdown: PROFILESELECTION.CARDS_CONTAINER,
          }}
        >
          <span>
            <img
              className={styles.settingsIcon}
              src={ParentZone}
              alt="Settings"
            />
          </span>
          Parent Zone
        </FocusDiv>
      </div>
      <FocusDiv
        ref={baseballCardsContainer}
        nav={{
          id: PROFILESELECTION.CARDS_CONTAINER,
          parent: PROFILESELECTION.PAGE,
          forwardFocus: profiles.length > 0 ? 'ITEM-0' : '',
          useLastFocus: true,
          nextup: 'PARENTALZONE',
          nextdown: PROFILE_CARDS_CONTAINER,
        }}
        className={styles.baseballCardsContainer}
      >
        {baseballCards}
      </FocusDiv>
      <div>
        <Cards
          history={history}
          authenticateUserProfileContent={authenticateUserProfileContent}
          profiles={profiles}
          addProfile={addProfile}
        />
      </div>
    </Page>
  );
};

ProfileSelection.propTypes = {
  history: PropTypes.object,
};

export default withRouter(ProfileSelection);
